<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useManagementStore } from '@/store/management'
import { useSuccessStore } from '@/store/success'
import type { IForecastSaveRow, IManagementArea } from '@/interfaces/entities/management'

// Cadastro de previsto sem a estrutura de supervisório: o usuário BUSCA e
// SELECIONA uma área ou um sensor e anota o previsto (ponta/fora ponta) dos 12
// meses. Total calculado ao vivo.
const store = useManagementStore()

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

type Target = {
  key: string
  type: 'area' | 'sensor'
  label: string
  sublabel?: string
  areaId: number
  forecastableId: number
}

const year = computed({ get: () => store.forecastYear, set: (v: number) => store.setForecastYear(v) })
const yearOptions = computed(() => {
  const current = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => current - i)
})

// Lista plana de alvos (áreas + sensores) a partir da árvore — sem navegar colunas.
const targets = computed<Target[]>(() => {
  const list: Target[] = []
  const walk = (areas: IManagementArea[]) => {
    areas.forEach((area) => {
      list.push({ key: `area:${area.id}`, type: 'area', label: area.name, sublabel: 'Área', areaId: area.id, forecastableId: area.id })
      ;(area.management_area_sensors || []).forEach((s) => {
        list.push({
          key: `sensor:${s.id}`,
          type: 'sensor',
          label: s.sensor_name,
          sublabel: `Sensor · ${area.name}`,
          areaId: area.id,
          forecastableId: s.id,
        })
      })
      walk(area.children || [])
    })
  }
  walk(store.roots)
  return list
})

const search = ref('')
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return targets.value
  return targets.value.filter((t) => `${t.label} ${t.sublabel}`.toLowerCase().includes(q))
})

const selected = ref<Target | null>(null)
type Cell = { ponta: string; fora: string }
const cells = reactive<Record<number, Cell>>({})

const parse = (value: string): number | null => {
  const cleaned = value?.toString().trim().replace(/\./g, '').replace(',', '.')
  if (!cleaned) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function cellTotal(month: number): number | null {
  const cell = cells[month]
  if (!cell) return null
  const p = parse(cell.ponta)
  const f = parse(cell.fora)
  if (p == null && f == null) return null
  return (p ?? 0) + (f ?? 0)
}

const yearTotal = computed(() => Array.from({ length: 12 }, (_, i) => cellTotal(i + 1) ?? 0).reduce((a, b) => a + b, 0))

function resetCells() {
  // Semeia 12 meses vazios SINCRONAMENTE para o grid não renderizar com cells
  // indefinido durante o await do fetch.
  for (let m = 1; m <= 12; m++) cells[m] = { ponta: '', fora: '' }
}

async function selectTarget(target: Target) {
  selected.value = target
  resetCells()
  const grid = (await store.fetchForecasts(target.areaId, year.value)) ?? store.forecastGrid(target.areaId, year.value)
  const data = target.type === 'area' ? grid?.area : grid?.sensors.find((s) => s.forecastable_id === target.forecastableId)
  for (let m = 1; m <= 12; m++) {
    const month = data?.months.find((x) => x.month === m)
    cells[m] = {
      ponta: month?.consumo_ponta == null ? '' : String(month.consumo_ponta),
      fora: month?.consumo_fora_ponta == null ? '' : String(month.consumo_fora_ponta),
    }
  }
}

// Ao trocar o ano, recarrega o alvo selecionado.
watch(year, () => {
  if (selected.value) selectTarget(selected.value)
})

function buildRows(): IForecastSaveRow[] {
  return Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    consumo_ponta: parse(cells[i + 1]?.ponta ?? ''),
    consumo_fora_ponta: parse(cells[i + 1]?.fora ?? ''),
  }))
}

async function save() {
  if (!selected.value) return
  const rows = buildRows()
  const payload =
    selected.value.type === 'area'
      ? { year: year.value, area: rows, sensors: {} }
      : { year: year.value, area: [], sensors: { [String(selected.value.forecastableId)]: rows } }

  const ok = await store.saveForecasts(selected.value.areaId, payload)
  if (ok) useSuccessStore().setSuccess(['Previsto salvo com sucesso.'])
}

const fmt = (v: number | null) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 }))
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center gap-3">
      <h3 class="text-base font-bold text-slate-900">Previsto de consumo</h3>
      <span class="text-sm text-slate-500">Busque e selecione uma área ou sensor e anote o previsto (ponta/fora ponta) de cada mês.</span>
      <span class="ml-auto text-sm text-slate-500">Ano</span>
      <select v-model.number="year" class="border border-stroke px-3 py-1.5 text-sm focus:border-primary focus:outline-none">
        <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
      </select>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
      <!-- Busca + lista de alvos -->
      <div class="flex flex-col border border-stroke bg-white">
        <input
          v-model="search"
          type="text"
          placeholder="Buscar área ou sensor…"
          class="border-b border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none" />
        <div class="max-h-[420px] overflow-y-auto">
          <button
            v-for="t in filtered"
            :key="t.key"
            type="button"
            class="flex w-full flex-col border-b border-stroke px-3 py-2 text-left transition hover:bg-slate-50"
            :class="{ 'bg-primary/5': selected?.key === t.key }"
            @click="selectTarget(t)">
            <span class="text-sm font-medium text-slate-800">{{ t.label }}</span>
            <span class="text-xs text-slate-400">{{ t.sublabel }}</span>
          </button>
          <p v-if="!filtered.length" class="px-3 py-6 text-center text-sm text-slate-400">Nada encontrado.</p>
        </div>
      </div>

      <!-- Grade do alvo selecionado -->
      <div v-if="selected" class="flex flex-col border border-stroke bg-white">
        <div class="flex items-center justify-between border-b border-stroke px-4 py-3">
          <div>
            <div class="text-sm font-semibold text-slate-800">{{ selected.label }}</div>
            <div class="text-xs text-slate-400">{{ selected.sublabel }} · {{ year }}</div>
          </div>
          <div class="text-sm text-slate-500">Total ano: <strong class="text-slate-800">{{ fmt(yearTotal) }} kWh</strong></div>
        </div>

        <div class="overflow-x-auto p-3">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs uppercase tracking-wide text-slate-400">
                <th class="px-3 py-2">Mês</th>
                <th class="px-3 py-2 text-right">Consumo Ponta (kWh)</th>
                <th class="px-3 py-2 text-right">Consumo Fora Ponta (kWh)</th>
                <th class="px-3 py-2 text-right">Total (kWh)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in 12" :key="i" class="border-t border-stroke">
                <td class="px-3 py-1.5 font-medium text-slate-700">{{ MONTH_LABELS[i - 1] }}</td>
                <td class="px-3 py-1.5">
                  <input
                    v-model="cells[i].ponta"
                    type="text"
                    inputmode="decimal"
                    placeholder="—"
                    class="h-9 w-full border border-stroke px-2 text-right focus:border-primary focus:outline-none" />
                </td>
                <td class="px-3 py-1.5">
                  <input
                    v-model="cells[i].fora"
                    type="text"
                    inputmode="decimal"
                    placeholder="—"
                    class="h-9 w-full border border-stroke px-2 text-right focus:border-primary focus:outline-none" />
                </td>
                <td class="px-3 py-1.5 text-right font-semibold text-slate-800">{{ fmt(cellTotal(i)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end border-t border-stroke p-3">
          <button
            type="button"
            :disabled="store.savingForecasts"
            class="bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90 disabled:opacity-50"
            @click="save">
            {{ store.savingForecasts ? 'Salvando…' : 'Salvar previsto' }}
          </button>
        </div>
      </div>

      <div v-else class="flex min-h-[200px] items-center justify-center border border-stroke bg-slate-50 text-center text-sm text-slate-400">
        Selecione uma área ou sensor na lista para cadastrar o previsto.
      </div>
    </div>
  </div>
</template>
